import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  Clock3,
  Eye,
  Info,
  LayoutGrid,
  List,
  MonitorSmartphone,
  MonitorPlay,
  MoreVertical,
  Plus,
  Power,
  PowerOff,
  Tv,
  RefreshCw,
  RotateCcw,
  Search,
  Stethoscope,
  Trash2,
  X,
  Check,
  FileBarChart,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type Device, type DeviceDetail, type LineupEntry, mediaUrl, type Package, type Room } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Pager, usePaged } from '../components/Pager';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, Modal, PageHeader, timeAgo } from '../components/ui';
import { AssignmentEditor } from './PackagesPage';
import { ask } from '../components/Confirm';

const PLATFORM: Record<string, string> = {
  WEBOS: 'LG webOS TV',
  ORSAY: 'Samsung Smart TV',
  ANDROID_STB: 'Android STB',
  ANDROID_TV: 'Android TV',
  GOOGLE_TV: 'Google TV',
  TIZEN: 'Samsung Tizen',
};
const platformName = (p: string) => PLATFORM[p] ?? p.replace(/_/g, ' ');
const modelOf = (d: Device) => [d.manufacturer, d.model].filter(Boolean).join(' ') || '—';
type DevState = 'online' | 'offline' | 'pending' | 'disabled';
const stateOf = (d: Device): DevState => (d.status !== 'ACTIVE' ? 'disabled' : !d.activated ? 'pending' : d.online ? 'online' : 'offline');
const STATE_LABEL: Record<DevState, string> = { online: 'Online', offline: 'Offline', pending: 'Not activated', disabled: 'Disabled' };

function StatePill({ d }: { d: Device }) {
  const s = stateOf(d);
  return (
    <span className={`state-pill ${s}`}>
      <span className="state-dot" /> {STATE_LABEL[s]}
    </span>
  );
}

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub?: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        {sub && <span className="stat-tile-sub">{sub}</span>}
      </span>
    </div>
  );
}

export function DevicesPage() {
  const site = useSite();
  const { can } = useSession();
  const qc = useQueryClient();
  const [activating, setActivating] = useState(false);
  return (
    <>
      <PageHeader
        title="Devices"
        sub="Manage TVs and STBs, monitor their health, and activate new ones. Status updates live."
        actions={
          site && (
            <>
              <button className="btn" title="Refresh" onClick={() => void qc.invalidateQueries({ queryKey: ['devices'] })}>
                <RefreshCw size={16} />
              </button>
              <Link className="btn" to="/devices/report">
                <FileBarChart size={16} /> TV operation reports
              </Link>
              {can('OPERATOR') && (
                <button className="btn primary" onClick={() => setActivating(true)}>
                  <Plus size={16} /> Activate device
                </button>
              )}
            </>
          )
        }
      />
      <NeedsSite>{site && <DeviceList siteId={site.id} />}</NeedsSite>
      {activating && site && <ActivateModal siteId={site.id} onClose={() => setActivating(false)} />}
    </>
  );
}

/** ⋮ menu on a device row / card. */
function DeviceMenu({ d, onCommand, onToggle, onDelete }: { d: Device; onCommand: (type: string) => void; onToggle: () => void; onDelete: () => void }) {
  const { can } = useSession();
  if (!can('OPERATOR')) return null;
  const item = (label: string, icon: ReactNode, run: () => void, danger = false) => (
    <button type="button" className={`more-item${danger ? ' danger-item' : ''}`} onClick={run}>
      {icon} {label}
    </button>
  );
  return (
    <Menu
      minWidth={190}
      button={({ onClick }) => (
        <button type="button" className="row-btn" title="More" onClick={onClick}>
          <MoreVertical size={16} />
        </button>
      )}
    >
      {item('Refresh config', <RefreshCw size={15} />, () => onCommand('REFRESH_CONFIG'))}
      {item('Restart app', <RotateCcw size={15} />, () => onCommand('RESTART_APP'))}
      {item('Reboot', <Power size={15} />, () => ask(`Reboot ${d.name}?`, () => onCommand('REBOOT')))}
      {can('ADMIN') && item(d.status === 'ACTIVE' ? 'Disable' : 'Enable', <Power size={15} />, onToggle)}
      {can('ADMIN') && item('Delete', <Trash2 size={15} />, onDelete, true)}
    </Menu>
  );
}

function DeviceList({ siteId }: { siteId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'all' | DevState>('all');
  const [room, setRoom] = useState('all');
  const [model, setModel] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [openId, setOpenId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const devices = useQuery({
    queryKey: ['devices', siteId],
    queryFn: () => api.get<{ items: Device[] }>(`/sites/${siteId}/devices`),
    refetchInterval: 30_000,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['devices'] });
  const command = useMutation({ mutationFn: ({ id, type }: { id: string; type: string }) => api.post(`/devices/${id}/commands`, { type }), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: (d: Device) => api.patch(`/devices/${d.id}`, { status: d.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (d: Device) => api.delete(`/devices/${d.id}`), onSuccess: refresh });
  const askDelete = (d: Device) =>
    ask(`Delete ${d.name} permanently? Its history goes too, and the TV goes back to the activation screen. To keep it in the list, use Disable instead.`, () => remove.mutate(d));
  const bulk = useMutation({
    mutationFn: async (type: string) => {
      for (const id of selected) await api.post(`/devices/${id}/commands`, { type });
    },
    onSuccess: () => {
      setSelected(new Set());
      refresh();
    },
  });
  const all = devices.data?.items ?? [];
  const opts = useMemo(
    () => ({
      rooms: [...new Set(all.map((d) => d.room?.number).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      models: [...new Set(all.map(modelOf))].sort(),
      platforms: [...new Set(all.map((d) => d.platform))].sort(),
    }),
    [all],
  );
  const items = all.filter((d) => {
    const s = q.trim().toLowerCase();
    if (s && ![d.name, d.room?.number, d.macAddress, d.ipAddress, d.model, d.serial].some((v) => v?.toLowerCase().includes(s))) return false;
    if (state !== 'all' && stateOf(d) !== state) return false;
    if (room !== 'all' && (room === 'none' ? !!d.room : d.room?.number !== room)) return false;
    if (model !== 'all' && modelOf(d) !== model) return false;
    if (platform !== 'all' && d.platform !== platform) return false;
    return true;
  });
  const { rows, pager } = usePaged(items, 10);
  const n = { total: all.length, online: all.filter((d) => stateOf(d) === 'online').length, offline: all.filter((d) => stateOf(d) === 'offline').length, pending: all.filter((d) => stateOf(d) === 'pending').length };
  const pct = (v: number) => `${n.total ? Math.round((v / n.total) * 100) : 0}% of all devices`;
  const pageIds = rows.map((d) => d.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const sel = (id: string) =>
    setSelected((cur) => {
      const x = new Set(cur);
      if (x.has(id)) x.delete(id);
      else x.add(id);
      return x;
    });
  if (devices.isLoading) return <Loading />;
  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<MonitorSmartphone size={20} />} tone="violet" value={n.total} label="Total devices" />
        <StatCard icon={<span className="big-dot green" />} tone="green" value={n.online} label="Online" sub={pct(n.online)} />
        <StatCard icon={<span className="big-dot grey" />} tone="grey" value={n.offline} label="Offline" sub={pct(n.offline)} />
        <StatCard icon={<Clock3 size={20} />} tone="amber" value={n.pending} label="Pending activation" sub={pct(n.pending)} />
      </div>
      <div className="card fill">
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search by name, room, MAC or IP…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={state} onChange={(e) => setState(e.target.value as 'all' | DevState)} aria-label="Status">
            <option value="all">All status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="pending">Not activated</option>
            <option value="disabled">Disabled</option>
          </Select>
          <Select value={room} onChange={(e) => setRoom(e.target.value)} aria-label="Room">
            <option value="all">All rooms</option>
            <option value="none">No room</option>
            {opts.rooms.map((r) => (
              <option key={r} value={r}>
                Room {r}
              </option>
            ))}
          </Select>
          <Select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Model">
            <option value="all">All models</option>
            {opts.models.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
          <Select value={platform} onChange={(e) => setPlatform(e.target.value)} aria-label="Platform">
            <option value="all">All platforms</option>
            {opts.platforms.map((p) => (
              <option key={p} value={p}>
                {platformName(p)}
              </option>
            ))}
          </Select>
          <span className="muted" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            {items.length} devices
          </span>
          <span className="view-toggle">
            <button type="button" className={view === 'list' ? 'on' : ''} title="List" onClick={() => setView('list')}>
              <List size={16} />
            </button>
            <button type="button" className={view === 'grid' ? 'on' : ''} title="Cards" onClick={() => setView('grid')}>
              <LayoutGrid size={16} />
            </button>
          </span>
        </div>

        {selected.size > 0 && can('OPERATOR') && (
          <div className="bulk-bar">
            <strong>{selected.size} selected</strong>
            <button className="btn small" onClick={() => bulk.mutate('REFRESH_CONFIG')}>
              <RefreshCw size={14} /> Refresh config
            </button>
            <button className="btn small" onClick={() => bulk.mutate('RESTART_APP')}>
              <RotateCcw size={14} /> Restart app
            </button>
            <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setSelected(new Set())} title="Clear selection">
              <X size={16} />
            </button>
          </div>
        )}

        {all.length === 0 ? (
          <Empty>No devices yet. Start the PulseIPTV app on a TV or STB, then click “Activate device” and enter the code it shows.</Empty>
        ) : items.length === 0 ? (
          <Empty>No device matches these filters.</Empty>
        ) : view === 'list' ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {can('OPERATOR') && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={allOnPage}
                        onChange={() =>
                          setSelected((cur) => {
                            const x = new Set(cur);
                            pageIds.forEach((id) => (allOnPage ? x.delete(id) : x.add(id)));
                            return x;
                          })
                        }
                        aria-label="Select this page"
                      />
                    </th>
                  )}
                  <th>Status</th>
                  <th>Device name</th>
                  <th>Room</th>
                  <th>Now playing</th>
                  <th>Model / platform</th>
                  <th>IP address</th>
                  <th>App version</th>
                  <th>Last seen</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="clickable" onClick={() => setOpenId(d.id)}>
                    {can('OPERATOR') && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(d.id)} onChange={() => sel(d.id)} aria-label={`Select ${d.name}`} />
                      </td>
                    )}
                    <td>
                      <StatePill d={d} />
                    </td>
                    <td className="cell-title">{d.name}</td>
                    <td>{d.room?.number ?? <span className="muted">—</span>}</td>
                    <td>
                      {d.currentChannel ? (
                        <>
                          <div className="cell-title">{d.currentChannel.name}</div>
                          <div className="cell-sub">CH {d.currentChannel.number}</div>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="cell-title">
                        {modelOf(d)}
                        {d.modelYear ? <span className="muted"> · {d.modelYear}</span> : null}
                      </div>
                      <div className="cell-sub">
                        {platformName(d.platform)}
                        {d.serial ? <span className="mono"> · SN {d.serial}</span> : null}
                      </div>
                    </td>
                    <td className="mono">{d.ipAddress ?? <span className="muted">—</span>}</td>
                    <td>{d.appVersion ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className={`state-dot inline ${d.online ? 'online' : 'offline'}`} /> {timeAgo(d.lastSeenAt)}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="row-btn" title="Details" onClick={(e) => (e.stopPropagation(), setOpenId(d.id))}>
                        <Eye size={16} />
                      </button>
                      <DeviceMenu d={d} onCommand={(type) => command.mutate({ id: d.id, type })} onToggle={() => toggle.mutate(d)} onDelete={() => askDelete(d)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="device-grid">
            {rows.map((d) => (
              <div key={d.id} className="device-card" onClick={() => setOpenId(d.id)}>
                <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                  <StatePill d={d} />
                  <DeviceMenu d={d} onCommand={(type) => command.mutate({ id: d.id, type })} onToggle={() => toggle.mutate(d)} onDelete={() => askDelete(d)} />
                </div>
                <div className="device-card-name">{d.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {d.room ? `Room ${d.room.number}` : 'No room'} · {platformName(d.platform)}
                </div>
                <div className="device-card-now">{d.currentChannel ? `${d.currentChannel.number} · ${d.currentChannel.name}` : 'Not on a channel'}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {modelOf(d)} · app {d.appVersion ?? '—'} · {timeAgo(d.lastSeenAt)}
                </div>
              </div>
            ))}
          </div>
        )}
        <Pager {...pager} />
        <ErrorBox error={command.error ?? toggle.error ?? bulk.error ?? remove.error} />
      </div>
      {openId && <DeviceDrawer id={openId} siteId={siteId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function ActivateModal({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const rooms = useQuery({ queryKey: ['rooms', siteId, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${siteId}/rooms`) });
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => api.get<{ items: Package[] }>('/packages') });
  const [code, setCode] = useState('');
  const [roomId, setRoomId] = useState('');
  const [name, setName] = useState('');
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const room = rooms.data?.items.find((r) => r.id === roomId);
  const digits = code.replace(/\D/g, '').slice(0, 6);
  const claim = useMutation({
    mutationFn: () =>
      api.post('/activations/claim', {
        code: digits,
        siteId,
        roomId: roomId || null,
        name: name || (room ? `${room.number}-STB` : 'STB'),
        // No package chosen = inherit from room / floor / site.
        ...(packageIds.length ? { packageIds } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['devices'] });
      onClose();
    },
  });
  return (
    <Modal
      title="Activate device"
      onClose={onClose}
      medium
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={digits.length !== 6 || claim.isPending} onClick={() => claim.mutate()}>
            {claim.isPending ? 'Activating…' : 'Activate'}
          </button>
        </>
      }
    >
      <div className="muted" style={{ marginTop: -6 }}>Enter the activation code shown on the TV and choose where it goes.</div>
      <Field label="Activation code">
        <input
          className="code-input"
          value={digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits}
          onChange={(e) => setCode(e.target.value)}
          placeholder="483 921"
          inputMode="numeric"
          autoFocus
        />
      </Field>
      <div className="grid cols-2">
        <Field label="Room">
          <Select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            <option value="">No room (public / lobby device)</option>
            {rooms.data?.items.map((r) => (
              <option key={r.id} value={r.id}>
                Room {r.number}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Device name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={room ? `${room.number}-STB` : 'Lobby TV 1'} />
        </Field>
      </div>
      <Field label="Content package (optional)" hint="Choose packages for this device only. Leave empty to use the room's (or floor's, or site's) packages.">
        <div className="pick-chips">
          {packages.data?.items.map((p) => {
            const on = packageIds.includes(p.id);
            return (
              <button key={p.id} type="button" className={`pick-chip${on ? ' on' : ''}`} onClick={() => setPackageIds(on ? packageIds.filter((x) => x !== p.id) : [...packageIds, p.id])}>
                {p.name}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="info-note">
        <Info size={16} /> If no package is selected, this device uses the packages of its room, floor or site.
      </div>
      <ErrorBox error={claim.error} />
    </Modal>
  );
}

type DrawerTab = 'overview' | 'channels' | 'wakeup' | 'activity';

function InfoTile({ label, value, mono, wide }: { label: string; value: ReactNode; mono?: boolean; wide?: boolean }) {
  return (
    <div className={`info-tile${wide ? ' wide' : ''}`}>
      <span className="info-tile-label">{label}</span>
      <span className={`info-tile-value${mono ? ' mono' : ''}`}>{value}</span>
    </div>
  );
}

/** A setting with its switch on the right and a short explanation under the title. */
/**
 * Wake-on-LAN address of an LG TV. TV off really switches off an LG that refuses warm standby, and a TV
 * that is off cannot hear TV on: a box online on the same site then sends the wake packet to this address.
 */
function WakeMacRow({ device, editable, onSave }: { device: DeviceDetail; editable: boolean; onSave: (v: string | null) => void }) {
  const [value, setValue] = useState(device.wakeMac ?? '');
  useEffect(() => setValue(device.wakeMac ?? ''), [device.wakeMac]);
  const dirty = value.trim().toLowerCase() !== (device.wakeMac ?? '');
  return (
    <div className="setting-row" style={{ flexWrap: 'wrap' }}>
      <span style={{ flex: '1 1 220px', minWidth: 0 }}>
        <span className="setting-title">Wake-on-LAN address</span>
        <span className="setting-desc">MAC of the network the TV uses (Wi-Fi or cable). TV on wakes it through an Android box online on this site.</span>
      </span>
      <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="20:3d:bd:3a:b3:8d" disabled={!editable} style={{ width: 170, height: 32, padding: '4px 8px' }} />
        {editable && dirty && (
          <button type="button" className="btn primary small" onClick={() => onSave(value.trim() || null)}>
            Save
          </button>
        )}
      </span>
    </div>
  );
}

/** Serial number: the TV reports it (LG, Android); for Samsung, staff type it from the label. */
function SerialInput({ value, onSave }: { value: string | null; onSave: (v: string | null) => void }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  const dirty = v.trim() !== (value ?? '');
  return (
    <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="From the TV label" style={{ maxWidth: 170, height: 32, padding: '4px 8px' }} />
      {dirty && (
        <button type="button" className="btn primary small" onClick={() => onSave(v.trim() || null)}>
          Save
        </button>
      )}
    </span>
  );
}

function SettingRow({ title, desc, on, editable, onChange, children }: { title: string; desc: string; on: boolean; editable: boolean; onChange: (v: boolean) => void; children?: ReactNode }) {
  return (
    <div className="setting-row">
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="setting-title">{title}</span>
        <span className="setting-desc">{desc}</span>
        {children}
      </span>
      <button type="button" className={`toggle${on ? ' on' : ''}`} role="switch" aria-checked={on} aria-label={title} disabled={!editable} onClick={() => onChange(!on)} />
    </div>
  );
}

function DeviceDrawer({ id, siteId, onClose }: { id: string; siteId: string; onClose: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  // The last command sent from this panel: its progress is shown live (sent → received → done).
  const [tracking, setTracking] = useState<{ id: string; type: string; label: string; at: number } | null>(null);
  const device = useQuery({
    queryKey: ['device', id],
    queryFn: () => api.get<DeviceDetail>(`/devices/${id}`),
    refetchInterval: (q) => {
      if (!tracking) return false;
      const c = q.state.data?.commands.find((x) => x.id === tracking.id);
      const finished = c && (c.status === 'ACKED' || c.status === 'FAILED' || c.status === 'EXPIRED');
      return finished || Date.now() - tracking.at > 10 * 60_000 ? false : 1500;
    },
  });
  const ent = useQuery({
    queryKey: ['device', id, 'entitlements'],
    queryFn: () => api.get<{ resolvedFrom: { scopeType: string }; packages: { name: string }[]; channels: { number: number; name: string }[] }>(`/devices/${id}/entitlements`),
  });
  const zap = useQuery({ queryKey: ['device', id, 'zap'], queryFn: () => api.get<{ samples: number; p50: number | null; p95: number | null }>(`/devices/${id}/zap-stats`) });
  const rooms = useQuery({ queryKey: ['rooms', siteId, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${siteId}/rooms`) });
  const lineup = useQuery({ queryKey: ['lineup', siteId], queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`) });
  const [tab, setTab] = useState<DrawerTab>('overview');
  const [openNumber, setOpenNumber] = useState('');
  const [editPkgs, setEditPkgs] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['device', id] });
    void qc.invalidateQueries({ queryKey: ['devices'] });
  };
  const command = useMutation({
    mutationFn: (body: { type: string; payload?: object; label?: string }) => api.post<{ id: string }>(`/devices/${id}/commands`, { type: body.type, payload: body.payload }),
    onSuccess: (res, body) => {
      setTracking({ id: res.id, type: body.type, label: body.label ?? COMMAND_LABEL[body.type] ?? body.type, at: Date.now() });
      refresh();
    },
  });
  const update = useMutation({ mutationFn: (body: object) => api.patch(`/devices/${id}`, body), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: () => api.post(`/devices/${id}/revoke`), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => api.delete(`/devices/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      onClose();
    },
  });
  const d = device.data;
  const playing = d?.currentChannel ? lineup.data?.items.find((e) => e.number === d.currentChannel!.number) : undefined;
  const art = playing?.channel.artwork ?? playing?.channel.logo;
  const op = can('OPERATOR');
  const remotes = ((d?.capabilities.remotes as TvRemote[] | undefined) ?? []).map((r) => `${r.name} (${r.kind === 'IR' ? 'IR' : 'Bluetooth'})${r.active ? ' · active' : ''}`);
  const configPending = !!d?.appliedConfigEtag && d.appliedConfigEtag !== d.expectedConfigEtag;

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <span className="pkg-section-icon">
            <MonitorSmartphone size={18} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="pkg-section-title">{d?.name ?? 'Device'}</span>
            <span className="pkg-section-sub">
              {d ? (
                <>
                  {d.room ? `Room ${d.room.number}` : 'No room'} · {platformName(d.platform)}
                </>
              ) : (
                'Device details and management'
              )}
            </span>
          </span>
          <button className="icon-btn" onClick={refresh} title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button className="icon-btn" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
        {!d ? (
          <Loading />
        ) : (
          <>
            <div className="drawer-summary">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <StatePill d={d} />
                <span className="muted" style={{ fontSize: 13 }}>
                  <span className={`state-dot inline ${d.online ? 'online' : 'offline'}`} /> Last seen {timeAgo(d.lastSeenAt)}
                </span>
              </div>
              <div className="now-card">
                <span className="now-art">{art ? <img src={mediaUrl(art.storageKey)} alt="" /> : <MonitorSmartphone size={22} />}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="muted" style={{ fontSize: 12 }}>Now playing</span>
                  <span className="now-title">{d.currentChannel?.name ?? 'Nothing'}</span>
                  {d.currentChannel && <span className="muted" style={{ fontSize: 12.5 }}>Channel {d.currentChannel.number}</span>}
                </span>
              </div>
              {op && (
                <div className="quick-actions">
                  <button type="button" onClick={() => command.mutate({ type: 'REFRESH_CONFIG' })}>
                    <RefreshCw size={18} />
                    <span>Refresh config</span>
                  </button>
                  <button type="button" onClick={() => command.mutate({ type: 'RESTART_APP' })}>
                    <RotateCcw size={18} />
                    <span>Restart app</span>
                  </button>
                  <button type="button" onClick={() => ask('Reboot this device?', () => command.mutate({ type: 'REBOOT' }))}>
                    <Power size={18} />
                    <span>Reboot</span>
                  </button>
                  <button type="button" onClick={() => command.mutate({ type: 'RUN_DIAGNOSTICS' })}>
                    <Stethoscope size={18} />
                    <span>Diagnostics</span>
                  </button>
                  {/* Android boxes that also run the PulsePanels signage player: what the TV shows. */}
                  {ANDROID_PLATFORMS.has(d.platform) && (
                    <>
                      <button type="button" onClick={() => command.mutate({ type: 'SHOW_IPTV' })}>
                        <Tv size={18} />
                        <span>Show IPTV</span>
                      </button>
                      <button type="button" onClick={() => command.mutate({ type: 'SHOW_SIGNAGE' })}>
                        <MonitorPlay size={18} />
                        <span>Show signage</span>
                      </button>
                    </>
                  )}
                  {/* TV power: Android boxes over HDMI-CEC; LG Pro:Centric TVs by themselves, woken with Wake-on-LAN. */}
                  {POWER_PLATFORMS.has(d.platform) && (
                    <>
                      <button type="button" onClick={() => ask('Turn this TV off? It can be turned on again from here.', () => command.mutate({ type: 'TV_OFF' }))}>
                        <PowerOff size={18} />
                        <span>TV off</span>
                      </button>
                      <button type="button" onClick={() => command.mutate({ type: 'TV_ON' })}>
                        <Power size={18} />
                        <span>TV on</span>
                      </button>
                    </>
                  )}
                </div>
              )}
              {tracking && <CommandProgress cmd={d.commands.find((c) => c.id === tracking.id)} label={tracking.label} type={tracking.type} online={d.online} deviceId={id} onClose={() => setTracking(null)} />}
              <ErrorBox error={command.error ?? update.error} />
            </div>

            <div className="drawer-tabs">
              {(
                [
                  ['overview', 'Overview'],
                  ['channels', `Channels (${ent.data?.channels.length ?? '…'})`],
                  ['wakeup', 'Wake-up call'],
                  ['activity', 'Activity'],
                ] as [DrawerTab, string][]
              ).map(([k, label]) => (
                <button key={k} type="button" className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                  {label}
                </button>
              ))}
            </div>

            <div className="drawer-body">
              {tab === 'overview' && (
                <>
                  <div className="info-grid">
                    <InfoTile
                      label="Room"
                      value={
                        op ? (
                          <Select value={d.roomId ?? ''} size="sm" onChange={(e) => update.mutate({ roomId: e.target.value || null })}>
                            <option value="">No room</option>
                            {rooms.data?.items.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.number}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          d.room?.number ?? '—'
                        )
                      }
                    />
                    <InfoTile label="App version" value={d.appVersion ?? '—'} />
                    <InfoTile
                      label="Model"
                      value={`${modelOf(d)}${d.osVersion ? ` · ${d.platform.startsWith('ANDROID') || d.platform === 'GOOGLE_TV' ? `Android ${d.osVersion}` : d.osVersion}` : ''}`}
                    />
                    <InfoTile label="Model year" value={d.modelYear ? String(d.modelYear) : '—'} />
                    <InfoTile
                      label="Serial number"
                      mono
                      value={
                        op ? (
                          <SerialInput value={d.serial} onSave={(v) => update.mutate({ serial: v })} />
                        ) : (
                          d.serial ?? '—'
                        )
                      }
                    />
                    <InfoTile label="Uptime" value={d.bootedAt ? timeAgo(d.bootedAt).replace(' ago', '') : '—'} />
                    <InfoTile label="IP address" value={d.ipAddress ?? '—'} mono />
                    <InfoTile label="MAC address" value={d.macAddress ?? '—'} mono />
                    <InfoTile
                      label="Channel change time"
                      value={zap.data?.samples ? `${((zap.data.p50 ?? 0) / 1000).toFixed(1)} s typical` : 'No data yet'}
                    />
                    <InfoTile
                      label="Configuration"
                      value={configPending ? <span className="badge warn">Update pending</span> : d.appliedConfigEtag ? <span className="badge ok">Up to date</span> : 'None'}
                    />
                  </div>

                  <div className="setting-list">
                    <SettingRow
                      title="Remote pairing"
                      desc={
                        d.capabilities.remoteLearning === true
                          ? 'Shows a Remotes page in the TV menu to learn IR remotes and pair Bluetooth ones.'
                          : "This TV's app doesn't support remote pairing (needs app 0.7 or later)."
                      }
                      on={d.remoteLearning}
                      editable={op}
                      onChange={(v) => update.mutate({ remoteLearning: v })}
                    >
                      {remotes.length > 0 && <span className="setting-extra">{remotes.join(', ')}</span>}
                    </SettingRow>
                    <SettingRow
                      title="Photos on channel cards"
                      desc={d.cardPictures ? 'Channel cards show photos and logos.' : 'Light mode: logos only, for older or slower TVs.'}
                      on={d.cardPictures}
                      editable={op}
                      onChange={(v) => update.mutate({ cardPictures: v })}
                    />
                    {d.platform === 'WEBOS' && <WakeMacRow device={d} editable={op} onSave={(v) => update.mutate({ wakeMac: v })} />}
                  </div>
                  {zap.data?.samples ? (
                    <div className="muted" style={{ fontSize: 12 }}>
                      Channel changes: typical {zap.data.p50} ms, slowest 5% {zap.data.p95} ms, over {zap.data.samples} changes. Config {d.appliedConfigEtag ?? 'none'}.
                    </div>
                  ) : null}
                </>
              )}

              {tab === 'channels' && (
                <>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {ent.data ? `This TV gets ${ent.data.channels.length} channels from its ${ent.data.resolvedFrom.scopeType.toLowerCase()}'s packages:` : 'Loading…'}
                  </div>
                  <div className="pick-chips">
                    {(ent.data?.packages ?? []).map((p) => (
                      <span key={p.name} className="pick-chip on" style={{ cursor: 'default' }}>
                        {p.name}
                      </span>
                    ))}
                    {ent.data && ent.data.packages.length === 0 && <span className="muted">No package</span>}
                  </div>
                  {can('ADMIN') && (
                    <div>
                      <button className="btn small" onClick={() => setEditPkgs((v) => !v)}>
                        {editPkgs ? 'Done' : 'Choose packages for this TV only'}
                      </button>
                    </div>
                  )}
                  {editPkgs && <AssignmentEditor siteId={siteId} scopeType="DEVICE" scopeId={id} />}
                  {op && (
                    <div className="drawer-card">
                      <span className="setting-title">Open a channel on this TV</span>
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <Select value={openNumber} onChange={(e) => setOpenNumber(e.target.value)} style={{ flex: 1 }}>
                          <option value="">Choose a channel…</option>
                          {lineup.data?.items.map((e) => (
                            <option key={e.id} value={e.number}>
                              {e.number} {e.channel.name}
                            </option>
                          ))}
                        </Select>
                        <button className="btn primary" disabled={!openNumber} onClick={() => command.mutate({ type: 'OPEN_CHANNEL', payload: { number: Number(openNumber) }, label: `Open channel ${openNumber}` })}>
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === 'wakeup' && <WakeupPanel deviceId={id} channels={lineup.data?.items ?? []} editable={op} />}

              {tab === 'activity' && (
                <>
                  <span className="setting-title">Recent commands</span>
                  {d.commands.length === 0 ? (
                    <div className="muted">None yet.</div>
                  ) : (
                    <div className="cmd-list">
                      {d.commands.slice(0, 20).map((c) => (
                        <div key={c.id} className="cmd-row">
                          <span className="cmd-name">{c.type.replace(/_/g, ' ').toLowerCase()}</span>
                          <span className={`badge${c.status === 'ACKED' ? ' ok' : c.status === 'FAILED' ? ' danger' : ''}`}>{c.status === 'ACKED' ? 'done' : c.status.toLowerCase()}</span>
                          <span className="muted cmd-time">{timeAgo(c.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {can('ADMIN') && (
                    <div className="danger-zone">
                      <span className="setting-title">Device access</span>
                      <span className="setting-desc">
                        Disable keeps the TV in the list but stops it until you enable it again. Revoke sends it back to the activation screen. Delete removes it and its
                        history for good.
                      </span>
                      <div className="row">
                        {d.status === 'ACTIVE' ? (
                          <button className="btn small" onClick={() => update.mutate({ status: 'DISABLED' })}>
                            Disable
                          </button>
                        ) : (
                          <button className="btn small" onClick={() => update.mutate({ status: 'ACTIVE' })}>
                            Enable
                          </button>
                        )}
                        <button className="btn small danger" onClick={() => ask('Revoke this device? The TV returns to the activation screen.', () => revoke.mutate())}>
                          Revoke activation
                        </button>
                        <button
                          className="btn small danger"
                          onClick={() =>
                            ask(`Delete ${d.name} permanently? Its history goes too, and the TV goes back to the activation screen. To keep it in the list, use Disable instead.`, () => remove.mutate())
                          }
                        >
                          <Trash2 size={14} /> Delete device
                        </button>
                      </div>
                      <ErrorBox error={remove.error ?? revoke.error} />
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

type TvRemote = { name: string; kind: 'IR' | 'BLUETOOTH'; active: boolean };

/** Front-desk view of the room's wake-up call (guests can also set it on the TV). */
const COMMAND_LABEL: Record<string, string> = {
  REFRESH_CONFIG: 'Refresh config',
  RESTART_APP: 'Restart app',
  REBOOT: 'Reboot',
  RUN_DIAGNOSTICS: 'Diagnostics',
  POWER_CYCLE: 'Restart TV (smart plug)',
  OPEN_CHANNEL: 'Open channel',
  SHOW_IPTV: 'Show IPTV',
  SHOW_SIGNAGE: 'Show signage',
  WAKE_ON_LAN: 'Wake a TV',
  REMOTE_KEY: 'Remote key for a TV',
  TV_OFF: 'TV off',
  TV_ON: 'TV on',
};

/** Platforms whose TV app can hand the screen to the PulsePanels signage player and drive HDMI-CEC. */
const ANDROID_PLATFORMS = new Set(['ANDROID_STB', 'ANDROID_TV', 'GOOGLE_TV']);
/** TV off/on: Android boxes (HDMI-CEC), LG Pro:Centric TVs (themselves), Samsung Tizen TVs (keys from a box on the site). */
const POWER_PLATFORMS = new Set([...ANDROID_PLATFORMS, 'WEBOS', 'TIZEN']);

/** Live progress of the last command: queued → sent to the TV → done (or failed / expired), then its result. */
function CommandProgress({
  cmd,
  label,
  type,
  online,
  deviceId,
  onClose,
}: {
  cmd?: { id: string; status: string; createdAt: string; sentAt?: string | null; ackedAt: string | null; result?: unknown };
  label: string;
  type: string;
  online: boolean;
  deviceId: string;
  onClose: () => void;
}) {
  const done = cmd?.status === 'ACKED';
  const diag = useQuery({
    queryKey: ['device', deviceId, 'diagnostics', cmd?.ackedAt],
    queryFn: () => api.get<{ items: { occurredAt: string; data: Record<string, unknown> }[] }>(`/devices/${deviceId}/events?type=diagnostics&limit=1`),
    enabled: type === 'RUN_DIAGNOSTICS' && done,
    // The TV sends the result a moment after confirming: wait for one newer than the command.
    refetchInterval: (q) => {
      const at = q.state.data?.items[0]?.occurredAt;
      const fresh = !!at && !!cmd && new Date(at).getTime() >= new Date(cmd.createdAt).getTime() - 5000;
      return fresh || !cmd || Date.now() - new Date(cmd.createdAt).getTime() > 90_000 ? false : 2000;
    },
  });
  const secs = (a?: string | null, b?: string | null) => (a && b ? Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000)) : null);
  const steps: { key: string; text: string; state: 'done' | 'now' | 'todo' | 'bad' }[] = [
    { key: 'q', text: 'Sent from the admin', state: 'done' },
    {
      key: 's',
      text: cmd?.sentAt ? `Received by the TV${secs(cmd.createdAt, cmd.sentAt) !== null ? ` (${secs(cmd.createdAt, cmd.sentAt)} s)` : ''}` : online ? 'Waiting for the TV to pick it up…' : 'The TV is offline: it gets it when it is back (within 24 h)',
      state: cmd?.sentAt ? 'done' : cmd?.status === 'EXPIRED' ? 'bad' : 'now',
    },
    {
      key: 'a',
      text:
        cmd?.status === 'ACKED'
          ? type === 'REBOOT'
            ? 'Done: the TV restarted and is back'
            : `Done by the TV${secs(cmd.sentAt ?? cmd.createdAt, cmd.ackedAt) !== null ? ` (${secs(cmd.sentAt ?? cmd.createdAt, cmd.ackedAt)} s)` : ''}`
          : cmd?.status === 'FAILED'
            ? 'The TV could not do it'
            : cmd?.status === 'EXPIRED'
              ? 'Expired: the TV did not pick it up in time'
              : type === 'REBOOT' && cmd?.sentAt
                ? 'Restarting… confirmed when the TV is back'
                : 'Waiting for the TV to confirm',
      state: cmd?.status === 'ACKED' ? 'done' : cmd?.status === 'FAILED' || cmd?.status === 'EXPIRED' ? 'bad' : cmd?.sentAt ? 'now' : 'todo',
    },
  ];
  const latest = diag.data?.items[0];
  const d = latest && cmd && new Date(latest.occurredAt).getTime() >= new Date(cmd.createdAt).getTime() - 5000 ? latest.data : undefined;
  return (
    <div className={`cmd-progress${done ? ' ok' : cmd?.status === 'FAILED' || cmd?.status === 'EXPIRED' ? ' bad' : ''}`}>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
        <strong>{label}</strong>
        <button className="icon-btn" onClick={onClose} title="Hide">
          <X size={14} />
        </button>
      </div>
      {steps.map((st) => (
        <div key={st.key} className={`cmd-step ${st.state}`}>
          <span className="cmd-dot">{st.state === 'done' ? <Check size={11} /> : st.state === 'bad' ? <X size={11} /> : null}</span>
          {st.text}
        </div>
      ))}
      {type === 'RUN_DIAGNOSTICS' && done && !d && <div className="muted">Waiting for the TV's report…</div>}
      {d && (
        <div className="cmd-result">
          {Object.entries(d)
            .filter(([, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => (
              <span key={k}>
                <span className="muted">{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span> {String(v)}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

function WakeupPanel({ deviceId, channels, editable }: { deviceId: string; channels: LineupEntry[]; editable: boolean }) {
  const qc = useQueryClient();
  const wake = useQuery({
    queryKey: ['device', deviceId, 'wakeup'],
    queryFn: () => api.get<{ time: string; enabled: boolean; daily: boolean; channelNumber: number | null; setBy: string } | null>(`/devices/${deviceId}/wakeup`),
  });
  const [time, setTime] = useState('');
  const [channel, setChannel] = useState('');
  const [daily, setDaily] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['device', deviceId, 'wakeup'] });
  const save = useMutation({
    mutationFn: () => api.put(`/devices/${deviceId}/wakeup`, { time, daily, channelNumber: channel ? Number(channel) : null, enabled: true }),
    onSuccess: invalidate,
  });
  const clear = useMutation({ mutationFn: () => api.delete(`/devices/${deviceId}/wakeup`), onSuccess: invalidate });
  const w = wake.data;
  return (
    <>
      <div className="drawer-card wake-current">
        <span className="pkg-section-icon">
          <Clock3 size={18} />
        </span>
        <span>
          <span className="setting-title">{w ? `${w.time}${w.daily ? ', every day' : ''}` : 'No wake-up call'}</span>
          <span className="setting-desc">
            {w ? `${w.channelNumber ? `Channel ${w.channelNumber}` : 'Last channel'} · set by ${w.setBy.toLowerCase()}` : 'Guests can also set one on the TV.'}
          </span>
        </span>
        {w && editable && (
          <button className="btn small danger" style={{ marginLeft: 'auto' }} onClick={() => clear.mutate()}>
            Clear
          </button>
        )}
      </div>
      {editable && (
        <div className="drawer-card">
          <span className="setting-title">{w ? 'Change the wake-up call' : 'Set a wake-up call'}</span>
          <div className="grid cols-2">
            <Field label="Time">
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>
            <Field label="Channel">
              <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="">Last channel</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.number}>
                    {c.number} {c.channel.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <label className="row" style={{ gap: 6, fontWeight: 500 }}>
              <input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} /> Every day
            </label>
            <button className="btn primary small" disabled={!time || save.isPending} onClick={() => save.mutate()}>
              Set wake-up call
            </button>
          </div>
        </div>
      )}
      <ErrorBox error={save.error ?? clear.error} />
    </>
  );
}
